import createTrackingRedirectUrl from '@clearscore/helpers.create-tracking-redirect-url';
import createEventContext from '@clearscore/helpers.create-event-context';
import interpolateValues from '@clearscore/helpers.interpolate-values';
import { getAllCookies } from '@clearscore/helpers.cookies';
import { getEnvs } from '@clearscore/helpers.envs';
import getDevice from '@clearscore/helpers.device';

const timeout = 150;
const isRedirect = ({ payload }) => payload && payload.redirect && payload.redirect.url;
const isInPage = ({ payload }) => payload && payload.trackInPage && payload.trackInPage.url;
const gotoUrl = ({ newTab, url }) => {
    if (newTab === false) {
        window.location.assign(url);
    } else {
        window.open(url, '_blank');
    }
};

const toSnakeCase = (props) =>
    Object.keys(props).reduce(
        (prev, key) => ({
            ...prev,
            [key]: String(props[key])
                .replace(/ /g, '_')
                .toLowerCase(),
        }),
        {},
    );

const format = (type, props = {}) => {
    switch (type) {
        case 'snake-case':
            return toSnakeCase(props);
        default:
            return props;
    }
};

const getTrackingValues = (trackingObject, { payload }, state) => {
    const { tracking, context, e } = payload.redirect || payload.trackInPage;
    const eDictionary = createEventContext({ e, context });
    const { props, formatValues } = trackingObject || { props: tracking } || {};
    const returnedProps =
        typeof props === 'function'
            ? props({ state, device: getDevice(), cookies: getAllCookies(), envs: getEnvs(), context: eDictionary })
            : props;
    const trackingProps = props ? interpolateValues(state, returnedProps, eDictionary) : {};
    return { props, tracking: format(formatValues, trackingProps) };
};

/**
 * Handle the redirect
 * @param {object} store redux store
 * @param {object} action redux action
 * @param {object} config tracking object container trackingKeys and event values
 * @returns {void}
 */
function handleRedirectTracking(store, action, config) {
    const { url, newTab } = action.payload.redirect;
    const { providers } = config[action.type] || { providers: {} };
    const state = store.getState();
    const { props, tracking } = getTrackingValues(providers.redirect, action, state);
    const trackingUrl = props ? createTrackingRedirectUrl(state, { url, tracking }) : url;
    gotoUrl({ url: trackingUrl, newTab });
}

/**
 * Handle the tracking in-page
 * @param {object} store redux store
 * @param {object} action redux action
 * @param {object} config tracking object container trackingKeys and event values
 * @returns {void}
 */
function handleInPageTracking(store, action, config) {
    const { url, newTab } = action.payload.trackInPage;
    const { providers } = config[action.type] || { providers: {} };
    const state = store.getState();
    const { tracking } = getTrackingValues(providers.segment, action, state);

    window.analytics.track(tracking.e, tracking);
    setTimeout(() => gotoUrl({ url, newTab }), timeout);
}

/**
 * If action has correct signature, perform redirect
 * Dispatch success or error actions
 * @param {object} config tracking object
 * @returns {Function} middleware
 */
const redirectMiddleware = (config) => (store) => (next) => (action) => {
    switch (true) {
        case !!isRedirect(action):
            return handleRedirectTracking(store, action, config);
        case !!isInPage(action):
            return handleInPageTracking(store, action, config);
        default:
            return next(action);
    }
};

export default redirectMiddleware;
